import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import HighchartWidget from '../../components/highchartWidget';
import pieOptions from '../../components/charts/pieOptions';
import { AssetsPie } from '../../components/data/graphData';
import { setChartTitle } from '../../redux/slices/uiSlice';

export default function GraphContent({ chartData }) {
  const dispatch = useDispatch();
  const [layout, setLayout] = useState(calcLayout());   // {isLandscape, chartH, pieSize}

  // --- elegir dataset ---
  const getChartData = useMemo(() => {
    if (!chartData) return AssetsPie;
    if (chartData?.type === 'pie') return AssetsPie;
    if (chartData?.data && Array.isArray(chartData.data)) return chartData;
    return AssetsPie;
  }, [chartData]);

  // asegurar datos positivos en pie
  const absData = useMemo(() => toAbsolutePieSeries(getChartData), [getChartData]);

  useEffect(() => {
    dispatch(setChartTitle(getChartData.name || 'Chart'));
  }, [dispatch, getChartData.name]);

  // --- listeners de resize + orientación ---
  useEffect(() => {
    const mm = window.matchMedia('(orientation: landscape)');
    const onChange = () => setLayout(calcLayout());
    onChange();

    mm.addEventListener ? mm.addEventListener('change', onChange)
                        : mm.addListener(onChange);
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);

    return () => {
      mm.removeEventListener ? mm.removeEventListener('change', onChange)
                             : mm.removeListener(onChange);
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
    };
  }, []);

  const mergedPieOptions = useMemo(() => ({
    ...pieOptions,
    plotOptions: {
      ...(pieOptions.plotOptions || {}),
      pie: {
        ...(pieOptions.plotOptions?.pie || {}),
        size: layout.pieSize,             // <- % dinámico
        dataLabels: {
          ...(pieOptions.plotOptions?.pie?.dataLabels || {}),
          distance: layout.isLandscape ? 10 : 14
        },
      },
    },
  }), [pieOptions, layout]);

  return (
    <div className="chart-content-center">
      <div
        // remount (y reflow) al cambiar orientación/alto disponible
        key={`${layout.isLandscape}-${Math.round(layout.chartH)}`}
        style={{
          width: '100%',
          maxWidth: 960,
          height: layout.chartH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto'
        }}
      >
        <HighchartWidget
          type="pie"
          height={layout.chartH}
          series={[{ name: absData.name || 'Data', data: absData.data || [] }]}
          options={mergedPieOptions}
        />
      </div>
    </div>
  );
}

/* ------------------ helpers ------------------ */

function toAbsolutePieSeries(series) {
  if (!series || !Array.isArray(series.data)) return series;
  const absData = series.data.map((pt) => {
    if (typeof pt === 'number') return Math.abs(pt) || 0;
    if (Array.isArray(pt) && pt.length >= 2) {
      const [name, y, ...rest] = pt;
      const n = Number(y);
      return [name, Number.isFinite(n) ? Math.abs(n) : 0, ...rest];
    }
    if (pt && typeof pt === 'object') {
      const n = Number(pt.y);
      return { ...pt, y: Number.isFinite(n) ? Math.abs(n) : 0 };
    }
    return pt;
  });
  return { ...series, data: absData };
}

function calcLayout() {
  const isLandscape = window.matchMedia('(orientation: landscape)').matches;

  const HEADER_H = isLandscape ? 40 : 52;
  const BOTTOM_H = isLandscape ? 0 : 96;
  const SIDEBAR_W = isLandscape ? 35 : 0;
  const PADDING_X = 32;                   
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const availW = vw - SIDEBAR_W - PADDING_X;
  const availH = vh - HEADER_H - BOTTOM_H - 24;
  const chartH = clamp(
    isLandscape ? Math.min(availH * 0.85, 340) : Math.min(availH * 0.62, 480),
    220,
    availH
  );
  const pieSize = isLandscape
    ? (availW < 560 ? '58%' : '62%')
    : (availW < 360 ? '70%' : '78%');

  return { isLandscape, chartH, pieSize };
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}
